import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PROJECT_V2 } from '../../lib/task-shell';
import { projectNavTarget } from '../ProjectPage/project-nav-targets';

/**
 * ⭐⭐ **프로젝트 메뉴 v2 의 ★ «문»** — 깃발이 꺼지면 ⇒ ⭐ 프로젝트 홈으로 되돌린다
 * (2026-10-08 · 1차 «뼈대». 전략 ⓒ 「깃발 뒤에 통째로」).
 *
 * ## ⛔ 404 가 ★ «아니다» — ⭐ 리다이렉트다
 *
 * ```
 * 이 판이 머지되면 ⇒ ★ 그 주소는 ⭐ «운영에 존재»한다(라우터에 줄이 있다)
 * ⇒ ⛔ 404 는 ★ 「없는 길」이라고 말하는데 ⇒ ⭐ 실은 ★ 「아직 «안 켠» 길」이다
 * ⇒ ⇒ ★ 그래서 ⭐ 「있는 곳」으로 보낸다. 선례: ApprovalPage/box/LegacyBoxRedirect.tsx
 * ```
 * ⚠ 가는 곳을 ★ `'/project'` 라고 ⛔ 글자로 적지 «않았다» — `projectNavTarget('home')` 한 곳에서 온다.
 *   ⇒ ★ 그 값은 ⭐ `project-nav-targets.test.ts` 가 «라우터 선언»과 맞대 준다(죽은 이동을 막는다).
 *
 * ## ⚠ 깃발이 ★ «켜졌을» 때 — ⭐ 1차에서는 ★ 아무것도 안 그린다
 *
 * ```
 * 1차는 ⭐ «뼈대»다 ⇒ ⛔ 화면을 «하나도» 담지 않는다(그것이 1차의 정의이고,
 *   그 정의를 ★ 「기존 파일이 한 글자도 안 바뀌었다」가 ⭐ 자동으로 지킨다)
 * ⇒ ★ 그래서 `children` 이 ⭐ 없으면 ★ null 이다 — ⚠ 그 갈래는 ⭐ «지금 쓰이는» 갈래다
 *   (라우터가 자식 없이 두 번 쓴다) ⇒ ⛔ 죽은 코드가 아니다.
 * ⇒ ⭐ 2차가 ★ 그 자리를 «채운다» — `<ProjectV2Route><무엇></ProjectV2Route>`.
 * ```
 *
 * ## ⭐⭐ 2026-10-08 — ★ `fallback` 을 ⭐ «더했다». ⇒ ⛔ 「셸을 바꾸려고」가 ★ 아니다
 *
 * ```
 * ⭐ 지금 이 문을 쓰는 두 주소는 ★ «새 주소»다 ⇒ router/index.tsx 의
 *   project/checklist-templates (2차 2-2′) · project/space/:teamId (2차 2-1′)
 * ⇒ ⇒ ✅ 옛 화면이 ★ 없으므로 ⇒ ⭐ 꺼짐에 «홈으로» 보내도 ★ 잃는 것이 0 이다.
 *
 * ⛔⛔ 그런데 ★ 4-5(보드 ＋ 배선)가 감쌀 주소는 ⇒ ⭐ `project/my-tasks` 이고
 *   ⇒ ★ 그 자리에는 ⭐ «이미 쓰이는 화면»이 있다 — `<ProjectNavShell><MyTasksDetailPage /></ProjectNavShell>`.
 *   ⇒ ⇒ ⛔ 그대로 감싸면 ★ 깃발 «꺼짐»에서 ⇒ ⭐ 그 화면이 사라지고 ★ 홈으로 튕긴다 ⇒ ⇒ ★ 그것은 ⭐ «회귀»다.
 * ⇒ ✅ 그래서 ★ 꺼짐에 ⭐ «무엇을 그릴지»를 ★ 부르는 쪽이 줄 수 있게 했다(지운 줄 ⇒ 서명 한 줄).
 *
 * ⚠ 까닭을 ★ 적어 두는 뜻 — ⇒ ⭐ 오늘 아침 설계 전언의 ★ 「요약 다섯」 ① 이 ⭐ 이 일이었는데
 *   ⇒ ★ 그때 적힌 까닭은 ⭐ 「셸을 바꾸려면」이었고 ⇒ ⛔ 그 까닭은 ★ 틀렸다(셸 교체는 ⭐ 재서 접었다).
 *   ⇒ ⇒ ⭐ 「무엇을 할까」는 맞고 ★ 「왜」가 틀린 꼴이다 — ⇒ ⭐ 재지 않으면 ★ 그 가름이 안 보인다.
 * ```
 *
 * ## ⛔ 이 깃발을 ★ «여기서만» 읽는다
 *
 * 선언(`task-shell.ts`) ＋ 이 파일 ＝ ⭐ **둘**. ⇒ ★ `project-v2-flag.guard.test.ts` 가 그 수를
 * 못 박는다 — ⚠ 갈래가 ⛔ 번지면(새 화면 안에서 또 보면) ★ 「어디가 v2 인가」가 안 보인다.
 * ⇒ ★ 2차에서 화면을 담을 때도 ⭐ 그 화면 «안»에서 이 깃발을 다시 보지 말고
 *   ⇒ ⭐ **이 문이 고른 것을 받아** 쓰십시오.
 */
export function ProjectV2Route({ children, fallback }: {
  children?: ReactNode;
  /** ⭐ 깃발이 «꺼졌을» 때 그릴 것 — ⇒ ★ 안 주면 ⭐ 종전처럼 «홈으로 되돌린다». */
  fallback?: ReactNode;
}) {
  const { search, hash } = useLocation();
  if (!PROJECT_V2) {
    /*
     * ⭐ 더한 줄 ★ 하나 — ⇒ ⛔ 아래 Navigate 는 ★ 한 글자도 ⭐ 안 바꾼다.
     *
     * ⚠⚠ `fallback !== undefined` 다 — ⛔ `fallback ?` 가 ★ 아니다.
     *   ⇒ 까닭: 누가 ★ `fallback={null}`(= ⭐ 「아무것도 그리지 말라」)을 주면
     *     ⇒ ⇒ ⛔ `fallback ?` 는 ★ 그것을 거짓으로 보고 ⇒ ⭐ «홈으로 보낸다».
     *   ⇒ ⇒ ★ 이 레포가 ⭐ 그 꼴로 데였다 — [[narrow-type-hides-the-null-path]].
     *   ⇒ ⇒ ⭐ 그래서 ★ 시험에 ⭐ `fallback={null}` 갈래를 «넣어» 두었다(그 줄이 ★ 이 가름을 잰다).
     */
    if (fallback !== undefined) return <>{fallback}</>;
    // ⚠ 쿼리·해시를 버리지 않는다 — 선례(LegacyBoxRedirect)와 같은 까닭: 북마크가 살아 있다.
    return <Navigate to={`${projectNavTarget('home')}${search}${hash}`} replace />;
  }
  return <>{children ?? null}</>;
}
